import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Building2, Check } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { ProfileSettingsForm } from '@/features/Profile/ProfileSettingsForm';
import { useOrgProfile, useUpdateOrgName, useOrgOverview } from '@/lib/hooks/useOrg';

export const Route = createFileRoute('/org/settings')({
  component: OrgSettingsPage,
});

function OrgSettingsPage() {
  const { data: org, isLoading } = useOrgProfile();
  const { data: overview } = useOrgOverview();
  const rename = useUpdateOrgName();
  const [name, setName] = useState('');

  useEffect(() => {
    if (org) setName(org.name);
  }, [org]);

  const dirty = org != null && name.trim() !== org.name && name.trim().length > 0;

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="text-[32px] font-bold leading-tight text-[#111827]">Settings</h1>
        <p className="pt-1 text-base text-[#6b7280]">Manage your organization and your account.</p>
      </div>

      {/* Organization */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#9ca3af]">
          <Building2 className="h-4 w-4" /> Organization
        </h2>
        {isLoading || !org ? (
          <Skeleton className="h-40 rounded-[24px]" />
        ) : (
          <OrgCard className="p-6">
            <div className="flex items-center gap-4">
              <OrgAvatar name={org.name} size={48} />
              <div>
                <p className="text-lg font-semibold text-[#111827]">{org.name}</p>
                <p className="text-xs text-[#9ca3af]">/{org.slug} · created {org.created_at ? format(new Date(org.created_at), 'dd MMM yyyy') : '—'}</p>
              </div>
            </div>

            <label className="mt-5 block">
              <span className="mb-1 block text-sm font-medium text-[#374151]">Organization name</span>
              <div className="flex gap-2">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10 flex-1 rounded-xl border border-[#ececec] bg-white px-3 text-sm text-[#111827] focus:border-[#ff5a1f] focus:outline-none"
                />
                <button
                  type="button"
                  disabled={!dirty || rename.isPending}
                  onClick={() => rename.mutate(name.trim())}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#ff5a1f] px-4 text-sm font-semibold text-white disabled:opacity-40"
                >
                  {rename.isSuccess && !dirty ? <Check className="h-4 w-4" /> : null}
                  {rename.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
              {rename.isError && <span className="mt-1 block text-xs text-red-600">Couldn&apos;t update the name.</span>}
            </label>

            {overview && (
              <div className="mt-5 grid grid-cols-2 gap-4 border-t border-[#ececec] pt-4 sm:grid-cols-4">
                <Stat label="Batches" value={overview.total_batches} />
                <Stat label="Students" value={overview.total_students} />
                <Stat label="Trainers" value={overview.total_trainers} />
                <Stat label="Avg completion" value={`${Math.round(overview.avg_completion_pct)}%`} />
              </div>
            )}
          </OrgCard>
        )}
      </section>

      {/* Personal account */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#9ca3af]">Your account</h2>
        <OrgCard className="p-6">
          <ProfileSettingsForm />
        </OrgCard>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xl font-bold text-[#111827]">{value}</p>
      <p className="text-xs text-[#9ca3af]">{label}</p>
    </div>
  );
}
